import { useCallback, useEffect, useState } from "react";
import { Send, Landmark, RefreshCw, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";
import { fmtMoney, fmtShortDate, cardCls, inputCls, labelCls } from "./accountingUtils";

const STATUS_CLS = {
  successful: "text-emerald-300", failed: "text-red-300", cancelled: "text-red-300",
};

/**
 * AccountingPayments — company books only (owners/superadmin). Send Interac
 * e-Transfers and direct deposits from the company VoPay account; every send
 * goes through an explicit confirmation.
 */
const AccountingPayments = () => {
  const confirm = useConfirm();
  const [status, setStatus] = useState(null);
  const [recips, setRecips] = useState({ etransfer: [], direct_deposit: [] });
  const [payments, setPayments] = useState([]);
  const [rail, setRail] = useState("etransfer");
  const [form, setForm] = useState({ amount: "", recipient_name: "", email: "", memo: "", deposit_key: "" });
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, r, p] = await Promise.all([
        api.get("/banking/company/vopay/status"),
        api.get("/banking/company/vopay/recipients"),
        api.get("/banking/company/vopay/payments"),
      ]);
      setStatus(s.data); setRecips(r.data); setPayments(p.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load payments");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const pickEmployee = (email) => {
    const emp = recips.etransfer.find((e) => e.email === email);
    setForm((s) => ({ ...s, email, recipient_name: emp?.name || s.recipient_name }));
  };

  const send = async () => {
    const amt = parseFloat(form.amount);
    if (!(amt > 0)) return toast.error("Enter an amount");
    let body, label;
    if (rail === "etransfer") {
      if (!form.recipient_name.trim() || !form.email.includes("@")) return toast.error("Recipient name and email are required");
      body = { amount: amt, recipient_name: form.recipient_name.trim(), email: form.email.trim(), memo: form.memo };
      label = `${fmtMoney(amt)} by Interac e-Transfer to ${body.recipient_name} (${body.email})`;
    } else {
      const d = recips.direct_deposit.find((x) => `${x.item_id}:${x.account_id}` === form.deposit_key);
      if (!d) return toast.error("Pick an employee account");
      body = { amount: amt, user_id: d.user_id, item_id: d.item_id, account_id: d.account_id, memo: form.memo };
      label = `${fmtMoney(amt)} by direct deposit to ${d.name} — ${d.label}`;
    }
    const ok = await confirm({ title: "Send this payment?", description: `${label}. Money leaves the company VoPay account${status?.env === "sandbox" ? " (sandbox — no real money moves)" : ""}.`, confirmLabel: "Send payment" });
    if (!ok) return;
    setSending(true);
    try {
      await api.post(`/banking/company/vopay/${rail === "etransfer" ? "etransfer" : "direct-deposit"}`, body);
      toast.success("Payment sent — status updates as the bank processes it");
      setForm({ amount: "", recipient_name: "", email: "", memo: "", deposit_key: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment failed");
    } finally {
      setSending(false);
    }
  };

  const refresh = async (p) => {
    setRefreshing(p.payment_id);
    try {
      await api.post(`/banking/company/vopay/payments/${p.payment_id}/refresh`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not refresh");
    } finally {
      setRefreshing(null);
    }
  };

  if (status && !status.configured) {
    return (
      <div className={`${cardCls} text-sm text-white/60`} data-testid="acct-payments-unconfigured">
        <div className="flex items-center gap-2 text-white"><Landmark className="h-4 w-4" /> Payments not set up</div>
        <p className="mt-2 text-xs">{status.detail}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="acct-payments">
      <div className={cardCls}>
        <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">VoPay account {status?.env ? `· ${status.env}` : ""}</div>
        <div className="mt-0.5 text-sm font-semibold text-white">Available to send</div>
        <div className="mt-2 font-mono text-3xl font-bold text-white" data-testid="acct-payments-balance">{status?.available != null ? fmtMoney(status.available) : status?.error ? "—" : <Loader2 className="h-6 w-6 animate-spin" />}</div>
        {status?.error && <p className="mt-1 text-xs text-red-300">{status.error}</p>}
      </div>

      <div className={`${cardCls} space-y-3`}>
        <div className="grid grid-cols-2 gap-1 rounded-md border border-white/10 bg-white/[0.02] p-1">
          {[["etransfer", "Interac e-Transfer"], ["direct_deposit", "Direct deposit"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setRail(k)} data-testid={`pay-rail-${k}`} className={`rounded px-2 py-1.5 font-mono text-[11px] uppercase tracking-widest ${rail === k ? "bg-[#1a3fd1] text-white" : "text-white/60 hover:text-white"}`}>{l}</button>
          ))}
        </div>
        <div>
          <label className={labelCls}>Amount $</label>
          <input type="text" inputMode="decimal" value={form.amount} onChange={(e) => set("amount", e.target.value.replace(/[^0-9.]/g, ""))} placeholder="0.00" data-testid="pay-amount" className={`mt-1 ${inputCls} font-mono`} />
        </div>
        {rail === "etransfer" ? (
          <>
            <div>
              <label className={labelCls}>Employee (or type any recipient below)</label>
              <select value="" onChange={(e) => pickEmployee(e.target.value)} data-testid="pay-employee" className={`mt-1 ${inputCls} bg-[#0a0a0a]`}>
                <option value="">Pick an employee…</option>
                {recips.etransfer.map((e) => <option key={e.employee_id} value={e.email}>{e.name} · {e.email}</option>)}
              </select>
              {(recips.etransfer_unverified || []).length > 0 && (
                <p className="mt-1 text-[11px] text-amber-200/80" data-testid="pay-unverified-note">
                  Not listed until office staff verify the email they entered themselves:{" "}
                  {recips.etransfer_unverified.map((e) => e.name).join(", ")}. Verify it on their Payroll profile.
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><label className={labelCls}>Recipient name</label><input value={form.recipient_name} onChange={(e) => set("recipient_name", e.target.value)} data-testid="pay-recipient-name" className={`mt-1 ${inputCls}`} /></div>
              <div><label className={labelCls}>Recipient email</label><input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="pay-recipient-email" className={`mt-1 ${inputCls}`} /></div>
            </div>
          </>
        ) : (
          <div>
            <label className={labelCls}>Employee bank account</label>
            <select value={form.deposit_key} onChange={(e) => set("deposit_key", e.target.value)} data-testid="pay-deposit-account" className={`mt-1 ${inputCls} bg-[#0a0a0a]`}>
              <option value="">Pick an account…</option>
              {recips.direct_deposit.map((d) => <option key={`${d.item_id}:${d.account_id}`} value={`${d.item_id}:${d.account_id}`}>{d.name} · {d.label}</option>)}
            </select>
            <p className="mt-1 text-[11px] text-white/40">Employees opt in under Home → $ → Banking ("Allow the company to direct-deposit…").</p>
          </div>
        )}
        <div><label className={labelCls}>Memo</label><input value={form.memo} onChange={(e) => set("memo", e.target.value)} data-testid="pay-memo" className={`mt-1 ${inputCls}`} /></div>
        <button type="button" onClick={send} disabled={sending} data-testid="pay-send" className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1a3fd1]/85 disabled:opacity-60">
          {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send {rail === "etransfer" ? "e-Transfer" : "direct deposit"}
        </button>
      </div>

      <div className="overflow-hidden rounded-lg border border-white/10">
        <div className="bg-white/[0.06] px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white/70">Payments sent</div>
        {payments.length === 0 ? (
          <div className="bg-white/[0.02] px-4 py-6 text-center text-sm text-white/50" data-testid="pay-history-empty">No payments yet.</div>
        ) : (
          <ul className="divide-y divide-white/10 bg-white/[0.02]">
            {payments.map((p) => (
              <li key={p.payment_id} className="flex items-center gap-3 px-4 py-3" data-testid={`pay-${p.payment_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{fmtShortDate(p.created_at)} · {p.rail === "etransfer" ? "e-Transfer" : "Direct deposit"}</div>
                  <div className="truncate text-sm font-semibold text-white">{p.recipient_name}</div>
                  <div className="truncate text-xs text-white/55">{p.recipient_email || p.account_label}{p.memo ? ` · ${p.memo}` : ""}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm font-semibold text-white">{fmtMoney(p.amount)}</div>
                  <div className={`font-mono text-[10px] uppercase tracking-widest ${STATUS_CLS[p.status] || "text-amber-300"}`}>{p.status}</div>
                </div>
                <button type="button" onClick={() => refresh(p)} disabled={refreshing === p.payment_id} data-testid={`pay-${p.payment_id}-refresh`} aria-label="Refresh status" className="rounded-md p-1.5 text-white/60 hover:bg-white/10 hover:text-white">
                  {refreshing === p.payment_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AccountingPayments;
